<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import leadsAPI from 'dashboard/api/nexceo/leads';
import nexceoProjectOptionsAPI from 'dashboard/api/nexceo/projectOptions';
import nexceoKanbanStagesAPI from 'dashboard/api/nexceo/kanbanStages';

const router = useRouter();
const route = useRoute();
const store = useStore();
const { t } = useI18n();
const { accountId } = useAccount();

// ── Mode detection ───────────────────────────────────────────────────────────
const editLeadId = route.params.id ? Number(route.params.id) : null;
const isEditMode = !!editLeadId;
const returnTo = route.query.return_to || 'kanban';
const stageId = route.query.stage_id || null;

// ── Supporting data ──────────────────────────────────────────────────────────
const projects = ref([]);
const stages = ref([]);
const agents = computed(() => store.getters['agents/getAgents']);

const isLoading = ref(false); // edit: loading existing lead
const isSaving = ref(false);
const saveError = ref('');
const saveResult = ref(null);

// ── Verification (create mode only) ─────────────────────────────────────────
const markVerified = ref(true);

// M29.3 Voice CRM Integration — the "Unknown Number → Convert to Lead" flow
// (PreCallContextPanel.vue) links here with the caller's name/phone as query
// params instead of duplicating lead-creation UI. Additive prefill only —
// every other field/behavior below is unchanged.
const prefillName = route.query.name || '';
const prefillPhoneNumber = route.query.phone_number || '';
// Sales Projects presentation — "Request More Information" on a unit links
// here with the project the customer was looking at preselected, same
// additive-prefill pattern as name/phone_number above.
const prefillProjectId = route.query.project_id || '';

// ── Form state ───────────────────────────────────────────────────────────────
const form = ref({
  name: prefillName,
  phone_number: prefillPhoneNumber,
  email: '',
  project_id: prefillProjectId,
  kanban_stage_id: stageId ? String(stageId) : '',
  assigned_agent_id: '',
  source_channel: '',
  budget_min: '',
  budget_max: '',
  currency: 'INR',
  preferred_location: '',
  property_type: '',
  bedrooms: '',
  purpose: '',
  timeline: '',
  priority: 'normal',
  next_follow_up_at: '',
  requirement_notes: '',
  verification_status: 'unverified',
});

// Track the original stage key so we only call move_stage when it changes.
const originalStageKey = ref(null);

const sendWelcome = ref(true); // default on — agent can uncheck if not wanted
const contactError = ref('');

// ── Project autofill (fill empty fields from project in both create and edit mode) ──
watch(
  () => form.value.project_id,
  newId => {
    if (!newId) return;
    const project = projects.value.find(p => String(p.id) === String(newId));
    if (!project) return;

    const autofillLocation = project.location || project.city || '';
    if (!form.value.preferred_location && autofillLocation)
      form.value.preferred_location = autofillLocation;
    if (!form.value.property_type && project.property_type)
      form.value.property_type = project.property_type;
    if (project.currency) form.value.currency = project.currency;
    if (!form.value.budget_min && project.price_min)
      form.value.budget_min = project.price_min;
    if (!form.value.budget_max && project.price_max)
      form.value.budget_max = project.price_max;
    const opts = project.bedroom_options;
    if (!form.value.bedrooms && Array.isArray(opts) && opts.length === 1)
      form.value.bedrooms = opts[0];
  }
);

// ── Requirement summary ──────────────────────────────────────────────────────
function requirementSummary() {
  if (form.value.requirement_notes) return form.value.requirement_notes;
  const parts = [];
  if (form.value.bedrooms) parts.push(form.value.bedrooms);
  if (form.value.preferred_location)
    parts.push(`in ${form.value.preferred_location}`);
  const bMin = Number(form.value.budget_min) || 0;
  const bMax = Number(form.value.budget_max) || 0;
  if (bMin || bMax) {
    const sym = form.value.currency === 'INR' ? '₹' : form.value.currency || '';
    const fmt = v => {
      if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
      if (v >= 100000) return `${(v / 100000).toFixed(0)}L`;
      return Number(v).toLocaleString();
    };
    if (bMin && bMax)
      parts.push(`budget ${sym}${fmt(bMin)} – ${sym}${fmt(bMax)}`);
    else parts.push(`budget ${sym}${fmt(bMin || bMax)}`);
  }
  return parts.join(', ') || 'Interested in project details';
}

// ── Budget preview ───────────────────────────────────────────────────────────
const budgetPreview = computed(() => {
  const bMin = Number(form.value.budget_min) || 0;
  const bMax = Number(form.value.budget_max) || 0;
  if (!bMin && !bMax) return '';
  const sym = form.value.currency === 'INR' ? '₹' : form.value.currency || '';
  const fmt = v => {
    if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
    if (v >= 100000) return `${(v / 100000).toFixed(0)}L`;
    return Number(v).toLocaleString();
  };
  if (bMin && bMax) return `${sym}${fmt(bMin)} – ${sym}${fmt(bMax)}`;
  return `${sym}${fmt(bMin || bMax)}`;
});

// ── Validation ───────────────────────────────────────────────────────────────
function validate() {
  contactError.value = '';
  if (!form.value.name.trim() && !form.value.phone_number.trim()) {
    contactError.value = 'Please enter at least a name or phone number.';
    return false;
  }
  // Verified leads require both phone AND email for WhatsApp + email confirmations.
  if (!isEditMode && markVerified.value) {
    if (!form.value.phone_number.trim()) {
      contactError.value =
        'Verified leads require a mobile number (for WhatsApp confirmation).';
      return false;
    }
    if (!form.value.email.trim()) {
      contactError.value =
        'Verified leads require an email address (for email confirmation).';
      return false;
    }
  }
  if (!isEditMode && sendWelcome.value && !form.value.phone_number.trim()) {
    contactError.value =
      'Phone number is required to send the WhatsApp welcome message.';
    return false;
  }
  return true;
}

// ── Navigation ───────────────────────────────────────────────────────────────
function goBack() {
  if (returnTo === 'verified_leads') {
    router.push({
      name: 'nexceo_verified_leads',
      params: { accountId: accountId.value },
    });
  } else if (returnTo === 'all_leads') {
    router.push({
      name: 'nexceo_all_leads',
      params: { accountId: accountId.value },
    });
  } else {
    router.push({
      name: 'nexceo_kanban',
      params: { accountId: accountId.value },
    });
  }
}

// ── Create / Update helpers (defined before onSave which calls them) ─────────
// Shared payload builder — extracted so the "Save as Draft" button (below)
// can reuse the exact same create payload instead of duplicating this
// field-cleanup logic.
function buildCreatePayload() {
  const payload = { ...form.value };
  if (!payload.budget_min) delete payload.budget_min;
  if (!payload.budget_max) delete payload.budget_max;
  if (!payload.project_id) delete payload.project_id;
  if (!payload.kanban_stage_id) delete payload.kanban_stage_id;
  if (!payload.assigned_agent_id) delete payload.assigned_agent_id;
  if (!payload.next_follow_up_at) delete payload.next_follow_up_at;
  [
    'email',
    'source_channel',
    'preferred_location',
    'property_type',
    'bedrooms',
    'purpose',
    'timeline',
    'requirement_notes',
    'verification_status',
  ].forEach(k => {
    if (!payload[k]) delete payload[k];
  });
  return payload;
}

async function onCreate() {
  const payload = buildCreatePayload();
  const { data } = await leadsAPI.createManual(payload, {
    markVerified: markVerified.value,
    sendWelcome: sendWelcome.value,
  });
  saveResult.value = data;
  useAlert(t('NEXCEO.LEAD.FORM.CREATED'));
  setTimeout(() => goBack(), 2000);
}

// "Save as Draft" — the existing implementation has no separate draft state
// on the backend, so this reuses the exact same create-lead API call
// (leadsAPI.createManual) with markVerified/sendWelcome forced off, rather
// than adding any new endpoint or business logic. A draft is simply an
// unverified lead that doesn't trigger the WhatsApp welcome message.
const isSavingDraft = ref(false);
async function onSaveAsDraft() {
  contactError.value = '';
  if (!form.value.name.trim() && !form.value.phone_number.trim()) {
    contactError.value = 'Please enter at least a name or phone number.';
    return;
  }
  saveError.value = '';
  saveResult.value = null;
  isSavingDraft.value = true;
  try {
    const payload = buildCreatePayload();
    const { data } = await leadsAPI.createManual(payload, {
      markVerified: false,
      sendWelcome: false,
    });
    saveResult.value = data;
    useAlert('Lead saved as draft.');
    setTimeout(() => goBack(), 2000);
  } catch (err) {
    const resp = err?.response?.data;
    const status = err?.response?.status || 'network';
    const msg = resp?.message || resp?.error || err?.message || 'Unknown error';
    saveError.value = `[${status}] ${msg}`;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  } finally {
    isSavingDraft.value = false;
  }
}

async function onUpdate() {
  const payload = { ...form.value };
  // Remove fields not editable via update
  delete payload.source_channel;
  delete payload.kanban_stage_id;
  if (!payload.budget_min) delete payload.budget_min;
  if (!payload.budget_max) delete payload.budget_max;
  if (!payload.project_id) delete payload.project_id;
  if (!payload.assigned_agent_id) delete payload.assigned_agent_id;
  if (!payload.next_follow_up_at) delete payload.next_follow_up_at;
  [
    'email',
    'preferred_location',
    'property_type',
    'bedrooms',
    'purpose',
    'timeline',
    'requirement_notes',
  ].forEach(k => {
    if (payload[k] === '') delete payload[k];
  });

  // Detect stage change: find new stage key from selected stage id
  const newStage = stages.value.find(
    s => String(s.id) === String(form.value.kanban_stage_id)
  );
  const newStageKey = newStage?.key || null;
  const stageChanged = newStageKey && newStageKey !== originalStageKey.value;

  try {
    const { data } = await leadsAPI.update(
      editLeadId,
      payload,
      stageChanged ? newStageKey : null
    );
    useAlert('Lead updated successfully.');
    // Update store so Kanban reflects changes immediately
    store.commit('nexceoLeads/UPDATE_LEAD', data);
    setTimeout(() => goBack(), 800);
  } catch (e) {
    const msg =
      e?.response?.data?.message ||
      e?.response?.data?.error ||
      e?.message ||
      'Unknown error';
    const status = e?.response?.status || 'network';
    saveError.value = `[${status}] ${msg}`;
  }
}

// ── Save ─────────────────────────────────────────────────────────────────────
async function onSave() {
  if (!validate()) return;
  saveError.value = '';
  saveResult.value = null;
  isSaving.value = true;

  try {
    if (isEditMode) {
      await onUpdate();
    } else {
      await onCreate();
    }
  } catch (err) {
    // Catch errors from onCreate() (onUpdate() handles its own errors internally).
    if (!isEditMode) {
      const resp = err?.response?.data;
      const status = err?.response?.status || 'network';
      const msg =
        resp?.message || resp?.error || err?.message || 'Unknown error';
      saveError.value = `[${status}] ${msg}`;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  } finally {
    isSaving.value = false;
  }
}

// ── Load existing lead (edit mode) — defined before onMounted which calls it ──
async function loadLead() {
  const { data } = await leadsAPI.show(editLeadId);
  // Pre-fill all form fields from the lead
  form.value = {
    name: data.name || '',
    phone_number: data.phone_number || '',
    email: data.email || '',
    project_id: data.project_id ? String(data.project_id) : '',
    kanban_stage_id: data.kanban_stage_id ? String(data.kanban_stage_id) : '',
    assigned_agent_id: data.assigned_agent_id || '',
    source_channel: data.source_channel || '',
    budget_min: data.budget_min || '',
    budget_max: data.budget_max || '',
    currency: data.currency || 'INR',
    preferred_location: data.preferred_location || '',
    property_type: data.property_type || '',
    bedrooms: data.bedrooms || '',
    purpose: data.purpose || '',
    timeline: data.timeline || '',
    priority: data.priority || 'normal',
    next_follow_up_at: data.next_follow_up_at
      ? data.next_follow_up_at.slice(0, 16)
      : '',
    requirement_notes:
      data.requirement_notes || data.metadata?.requirement_notes || '',
    verification_status: data.verification_status || 'unverified',
  };
  originalStageKey.value = data.kanban_stage?.key || null;
}

// ── Bootstrap ─────────────────────────────────────────────────────────────────
onMounted(async () => {
  isLoading.value = isEditMode;
  try {
    const [projectsRes, stagesRes] = await Promise.all([
      nexceoProjectOptionsAPI.get(),
      nexceoKanbanStagesAPI.getAll(),
      store.dispatch('agents/get'),
    ]);
    projects.value = projectsRes.data?.projects ?? [];
    stages.value = Array.isArray(stagesRes.data)
      ? stagesRes.data
      : (stagesRes.data?.stages ?? []);

    if (isEditMode) {
      await loadLead();
    }
  } finally {
    isLoading.value = false;
  }
});

// ── Computed helpers ─────────────────────────────────────────────────────────
const selectedProject = computed(
  () =>
    projects.value.find(p => String(p.id) === String(form.value.project_id)) ??
    null
);

const channelOptions = [
  'whatsapp',
  'email',
  'facebook',
  'instagram',
  'web_widget',
  'api',
  'other',
];
const purposeOptions = ['investment', 'self_use', 'rental', 'unknown'];
const timelineOptions = [
  'immediate',
  '1_month',
  '3_months',
  '6_months',
  'unknown',
];
const priorityOptions = ['urgent', 'high', 'normal', 'low'];
const currencyOptions = ['INR', 'USD', 'AED'];
const verificationOptions = [
  { value: 'unverified', label: 'Unverified' },
  { value: 'ai_verified', label: 'AI Verified' },
  { value: 'human_verified', label: 'Human Verified' },
  { value: 'rejected', label: 'Rejected' },
];

const projectBedroomOptions = computed(() => {
  const opts = selectedProject.value?.bedroom_options;
  return Array.isArray(opts) && opts.length > 1 ? opts : [];
});

const pageTitle = computed(() => (isEditMode ? 'Edit Lead' : 'Add Lead'));
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="flex flex-col w-full h-full overflow-y-auto bg-n-background">
    <!-- Page header -->
    <div
      class="flex items-center justify-between gap-3 px-6 py-4 border-b border-n-weak bg-n-background shrink-0 flex-wrap"
    >
      <div class="flex items-center gap-3 min-w-0">
        <button
          type="button"
          class="flex items-center justify-center w-8 h-8 rounded-lg text-n-slate-8 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors shrink-0"
          @click="goBack"
        >
          <span class="i-lucide-arrow-left size-5" />
        </button>
        <h1 class="text-xl font-bold text-n-slate-12 truncate">
          {{ pageTitle }}
        </h1>
        <span
          v-if="!isEditMode && markVerified"
          class="text-xs bg-green-100 text-green-700 px-2.5 py-1 rounded-full font-medium shrink-0"
        >
          Will be marked Verified
        </span>
        <span
          v-if="isEditMode"
          class="text-xs bg-yellow-100 text-yellow-700 px-2.5 py-1 rounded-full font-medium shrink-0"
        >
          Editing Lead #{{ editLeadId }}
        </span>
      </div>
      <div v-if="!isLoading" class="flex items-center gap-2 shrink-0">
        <button
          v-if="!isEditMode"
          type="button"
          :disabled="isSaving || isSavingDraft || !!saveResult"
          class="px-4 py-2 text-sm font-medium text-n-slate-11 !border !border-solid border-n-weak rounded-lg hover:bg-n-alpha-1 disabled:opacity-50 transition-colors"
          @click="onSaveAsDraft"
        >
          {{ isSavingDraft ? 'Saving…' : 'Save as Draft' }}
        </button>
        <button
          type="button"
          :disabled="isSaving || isSavingDraft || (!isEditMode && !!saveResult)"
          class="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-woot-500 hover:bg-woot-600 rounded-lg disabled:opacity-50 transition-colors"
          @click="onSave"
        >
          <span v-if="isSaving" class="i-lucide-loader-2 size-4 animate-spin" />
          <span
            v-else
            :class="isEditMode ? 'i-lucide-save' : 'i-lucide-user-plus'"
            class="size-4"
          />
          {{
            isSaving ? (isEditMode ? 'Saving…' : 'Creating…') : pageTitle
          }}
        </button>
      </div>
    </div>

    <!-- Loading state (edit mode: fetching existing lead) -->
    <div v-if="isLoading" class="flex-1 flex items-center justify-center">
      <div class="flex flex-col items-center gap-3 text-n-slate-7">
        <span class="i-lucide-loader-2 size-7 animate-spin text-woot-500" />
        <span class="text-sm">Loading lead…</span>
      </div>
    </div>

    <!-- Body -->
    <div v-else class="flex-1 px-6 py-5 bg-n-alpha-1">
      <!-- Save result banner (create mode) -->
      <div
        v-if="saveResult && !isEditMode"
        class="mb-5 rounded-xl border border-green-200 bg-green-50 px-5 py-4 space-y-1.5 text-sm"
      >
        <p class="font-semibold text-green-800 flex items-center gap-2">
          <span class="i-lucide-check-circle size-4" />
          Lead created successfully
        </p>
        <!-- Contact -->
        <p class="text-green-700 flex items-center gap-1.5">
          <span class="i-lucide-user size-3.5 text-green-600" />
          Contact:
          <strong>{{
            saveResult.contact_created ? 'Created' : 'Found existing'
          }}</strong>
          <span v-if="saveResult.contact_id"
            >(#{{ saveResult.contact_id }})</span
          >
        </p>
        <!-- Conversation -->
        <p
          class="flex items-center gap-1.5"
          :class="
            saveResult.conversation_id ? 'text-green-700' : 'text-yellow-700'
          "
        >
          <span
            :class="
              saveResult.conversation_id
                ? 'i-lucide-message-circle size-3.5 text-green-600'
                : 'i-lucide-alert-triangle size-3.5 text-yellow-600'
            "
          />
          Conversation:
          <strong>{{
            saveResult.conversation_id
              ? (saveResult.conversation_created
                  ? 'Created'
                  : 'Found existing') + ` (#${saveResult.conversation_id})`
              : 'Not created — configure a WhatsApp inbox in NEXCEO Settings'
          }}</strong>
        </p>
        <!-- WhatsApp welcome -->
        <p
          class="flex items-center gap-1.5"
          :class="
            saveResult.welcome_status === 'sent'
              ? 'text-green-700'
              : 'text-yellow-700'
          "
        >
          <span
            :class="
              saveResult.welcome_status === 'sent'
                ? 'i-lucide-send size-3.5 text-green-600'
                : 'i-lucide-alert-triangle size-3.5 text-yellow-600'
            "
          />
          WhatsApp welcome:
          <strong>{{ saveResult.welcome_status || 'skipped' }}</strong>
          <span
            v-if="
              saveResult.welcome_error && saveResult.welcome_status !== 'sent'
            "
            class="text-xs text-yellow-700"
          >
            — {{ saveResult.welcome_error }}
          </span>
        </p>
        <!-- Verification -->
        <p class="text-green-700 flex items-center gap-1.5">
          <span class="i-lucide-shield-check size-3.5 text-green-600" />
          Verification:
          <strong>{{
            saveResult.lead?.verification_status === 'human_verified'
              ? 'Human Verified ✓'
              : saveResult.lead?.verification_status || '—'
          }}</strong>
        </p>
        <!-- Google Sheets sync -->
        <p
          v-if="saveResult.google_sheets_sync_status"
          class="flex items-center gap-1.5 text-green-700"
        >
          <span class="i-lucide-table-2 size-3.5 text-green-600" />
          Google Sheets:
          <strong>{{ saveResult.google_sheets_sync_status }}</strong>
        </p>
        <p class="text-green-600 text-xs pt-1">Redirecting…</p>
      </div>

      <!-- API error -->
      <div
        v-if="saveError"
        class="mb-5 flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3"
      >
        <span class="i-lucide-alert-circle size-4 shrink-0 mt-0.5" />
        {{ saveError }}
      </div>

      <div class="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-5 items-start">
        <!-- ══════════════════════ LEFT COLUMN ══════════════════════ -->
        <div class="flex flex-col gap-5 min-w-0">
          <!-- 1. Contact Information -->
          <div class="section-card">
            <div class="section-title mb-4">
              <span
                class="section-badge bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-400"
              >
                <span class="i-lucide-user-round size-4" />
              </span>
              <div>
                <h2 class="text-sm font-bold text-n-slate-12">
                  1. Contact Information
                </h2>
                <p class="text-xs text-n-slate-8">
                  Basic details of your customer
                </p>
              </div>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label class="label"
                  >Customer Name <span class="text-red-500">*</span></label
                >
                <div class="relative">
                  <span class="i-lucide-user field-icon" />
                  <input
                    v-model="form.name"
                    type="text"
                    placeholder="Enter full name"
                    class="field !pl-9 !pr-3"
                    :class="contactError ? '!border-red-400' : ''"
                    @input="contactError = ''"
                  />
                </div>
              </div>
              <div>
                <label class="label"
                  >Phone Number <span class="text-red-500">*</span></label
                >
                <div class="relative">
                  <span class="i-lucide-phone field-icon" />
                  <input
                    v-model="form.phone_number"
                    type="text"
                    placeholder="+91 98765 43210"
                    class="field !pl-9 !pr-3"
                    :class="contactError ? '!border-red-400' : ''"
                    @input="contactError = ''"
                  />
                </div>
              </div>
              <div class="sm:col-span-2">
                <label class="label">Email</label>
                <div class="relative">
                  <span class="i-lucide-mail field-icon" />
                  <input
                    v-model="form.email"
                    type="email"
                    placeholder="customer@email.com"
                    class="field !pl-9 !pr-3"
                  />
                </div>
              </div>
            </div>
            <p
              v-if="contactError"
              class="mt-2 text-xs text-red-600 flex items-center gap-1"
            >
              <span class="i-lucide-alert-circle size-3.5" />{{ contactError }}
            </p>
          </div>

          <!-- 2. Assignment -->
          <div class="section-card">
            <div class="section-title mb-4">
              <span
                class="section-badge bg-woot-50 text-woot-600 dark:bg-woot-500/10 dark:text-woot-400"
              >
                <span class="i-lucide-briefcase size-4" />
              </span>
              <div>
                <h2 class="text-sm font-bold text-n-slate-12">
                  2. Assignment
                </h2>
                <p class="text-xs text-n-slate-8">
                  Assign lead to a project, stage, and agent
                </p>
              </div>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label class="label">Project</label>
                <div class="relative">
                  <span class="i-lucide-building-2 field-icon" />
                  <select
                    v-model="form.project_id"
                    class="field appearance-none !pl-9 !pr-3"
                  >
                    <option value="">— select project</option>
                    <option
                      v-for="p in projects"
                      :key="p.id"
                      :value="String(p.id)"
                    >
                      {{ p.name }}
                    </option>
                  </select>
                </div>
                <p class="mt-1 text-xs text-n-slate-6">
                  Auto-fills empty location, property type, and budget fields.
                </p>
              </div>
              <div>
                <label class="label">Kanban Stage</label>
                <div class="relative">
                  <span class="i-lucide-layers field-icon" />
                  <select
                    v-model="form.kanban_stage_id"
                    class="field appearance-none !pl-9 !pr-3"
                  >
                    <option value="">— auto (New Enquiry)</option>
                    <option
                      v-for="s in stages"
                      :key="s.id"
                      :value="String(s.id)"
                    >
                      {{ s.name }}
                    </option>
                  </select>
                </div>
                <p
                  v-if="isEditMode && form.kanban_stage_id"
                  class="mt-1 text-xs text-yellow-700 flex items-center gap-1"
                >
                  <span class="i-lucide-info size-3" />
                  Stage change goes through transition rules — invalid moves
                  are rejected.
                </p>
              </div>
              <div>
                <label class="label">Assigned Agent</label>
                <div class="relative">
                  <span class="i-lucide-user-round field-icon" />
                  <select
                    v-model="form.assigned_agent_id"
                    class="field appearance-none !pl-9 !pr-3"
                  >
                    <option value="">— unassigned</option>
                    <option v-for="a in agents" :key="a.id" :value="a.id">
                      {{ a.name }}
                    </option>
                  </select>
                </div>
              </div>
              <div v-if="!isEditMode">
                <label class="label">Source Channel</label>
                <div class="relative">
                  <span class="i-lucide-megaphone field-icon" />
                  <select
                    v-model="form.source_channel"
                    class="field appearance-none !pl-9 !pr-3"
                  >
                    <option value="">— select source —</option>
                    <option v-for="c in channelOptions" :key="c" :value="c">
                      {{ c }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <!-- 3. Requirement -->
          <div class="section-card">
            <div class="section-title mb-4">
              <span
                class="section-badge bg-green-50 text-green-600 dark:bg-green-500/10 dark:text-green-400"
              >
                <span class="i-lucide-home size-4" />
              </span>
              <div>
                <h2 class="text-sm font-bold text-n-slate-12">
                  3. Requirement
                </h2>
                <p class="text-xs text-n-slate-8">
                  Customer's preferred property details
                </p>
              </div>
            </div>

            <!-- Project autofill hint -->
            <p
              v-if="selectedProject"
              class="mb-3 text-xs text-woot-600 bg-woot-50 border border-woot-100 rounded-lg px-3 py-1.5 flex items-center gap-1.5"
            >
              <span class="i-lucide-zap size-3.5 shrink-0" />
              Empty requirement fields auto-filled from
              <strong>{{ selectedProject.name }}</strong
              >. You can edit them freely.
            </p>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label class="label">Preferred Location</label>
                <div class="relative">
                  <span class="i-lucide-map-pin field-icon" />
                  <input
                    v-model="form.preferred_location"
                    type="text"
                    placeholder="e.g. Patia, Bhubaneswar"
                    class="field !pl-9 !pr-3"
                  />
                </div>
              </div>
              <div>
                <label class="label">Bedrooms</label>
                <div class="relative">
                  <span class="i-lucide-bed-double field-icon" />
                  <input
                    v-model="form.bedrooms"
                    type="text"
                    placeholder="e.g. 3BHK"
                    class="field !pl-9 !pr-3"
                    :list="
                      projectBedroomOptions.length
                        ? 'bedroom-options-list'
                        : undefined
                    "
                  />
                </div>
                <datalist
                  v-if="projectBedroomOptions.length"
                  id="bedroom-options-list"
                >
                  <option
                    v-for="opt in projectBedroomOptions"
                    :key="opt"
                    :value="opt"
                  />
                </datalist>
              </div>
              <div>
                <label class="label">Property Type</label>
                <div class="relative">
                  <span class="i-lucide-building field-icon" />
                  <input
                    v-model="form.property_type"
                    type="text"
                    placeholder="e.g. apartment, villa"
                    class="field !pl-9 !pr-3"
                  />
                </div>
              </div>
              <div>
                <label class="label">Purpose</label>
                <div class="relative">
                  <span class="i-lucide-target field-icon" />
                  <select
                    v-model="form.purpose"
                    class="field appearance-none !pl-9 !pr-3"
                  >
                    <option value="">— select purpose —</option>
                    <option v-for="p in purposeOptions" :key="p" :value="p">
                      {{ p.replace('_', ' ') }}
                    </option>
                  </select>
                </div>
              </div>
              <div>
                <label class="label">Timeline</label>
                <div class="relative">
                  <span class="i-lucide-calendar field-icon" />
                  <select
                    v-model="form.timeline"
                    class="field appearance-none !pl-9 !pr-3"
                  >
                    <option value="">— select timeline —</option>
                    <option v-for="tl in timelineOptions" :key="tl" :value="tl">
                      {{ tl.replace(/_/g, ' ') }}
                    </option>
                  </select>
                </div>
              </div>
              <div>
                <label class="label">Priority</label>
                <div class="relative">
                  <span class="i-lucide-flag field-icon" />
                  <select
                    v-model="form.priority"
                    class="field appearance-none !pl-9 !pr-3"
                  >
                    <option v-for="p in priorityOptions" :key="p" :value="p">
                      {{ t(`NEXCEO.KANBAN.PRIORITY.${p}`) }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <!-- 4. Budget -->
          <div class="section-card">
            <div class="section-title mb-4">
              <span
                class="section-badge bg-yellow-50 text-yellow-600 dark:bg-yellow-500/10 dark:text-yellow-400"
              >
                <span class="i-lucide-wallet size-4" />
              </span>
              <div>
                <h2 class="text-sm font-bold text-n-slate-12">4. Budget</h2>
                <p class="text-xs text-n-slate-8">
                  Budget range for the property
                </p>
              </div>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label class="label">Currency</label>
                <select v-model="form.currency" class="field !px-3">
                  <option v-for="c in currencyOptions" :key="c" :value="c">
                    {{ c }}
                  </option>
                </select>
              </div>
              <div>
                <label class="label">Min Budget</label>
                <div class="relative">
                  <span class="i-lucide-banknote field-icon" />
                  <input
                    v-model="form.budget_min"
                    type="number"
                    min="0"
                    placeholder="e.g. 7000000"
                    class="field !pl-9 !pr-3"
                  />
                </div>
              </div>
              <div>
                <label class="label">Max Budget</label>
                <div class="relative">
                  <span class="i-lucide-banknote field-icon" />
                  <input
                    v-model="form.budget_max"
                    type="number"
                    min="0"
                    placeholder="e.g. 8000000"
                    class="field !pl-9 !pr-3"
                  />
                </div>
              </div>
            </div>
            <p v-if="budgetPreview" class="mt-2 text-xs text-n-slate-7">
              Preview:
              <span class="font-medium text-n-slate-10">{{
                budgetPreview
              }}</span>
            </p>
          </div>

          <!-- Verification status (edit mode only) -->
          <div v-if="isEditMode" class="section-card">
            <div class="section-title mb-3">
              <span
                class="section-badge bg-slate-100 text-slate-600 dark:bg-n-solid-3 dark:text-n-slate-11"
              >
                <span class="i-lucide-shield-check size-4" />
              </span>
              <h2 class="text-sm font-bold text-n-slate-12">Verification</h2>
            </div>
            <label class="label">Verification Status</label>
            <select v-model="form.verification_status" class="field !px-3 max-w-xs">
              <option
                v-for="v in verificationOptions"
                :key="v.value"
                :value="v.value"
              >
                {{ v.label }}
              </option>
            </select>
            <p class="mt-1 text-xs text-n-slate-7">
              Changing to "Human Verified" will record your name and the
              current time.
            </p>
          </div>
        </div>

        <!-- ══════════════════════ RIGHT COLUMN ══════════════════════ -->
        <div class="flex flex-col gap-5 min-w-0">
          <!-- 5. Additional Information -->
          <div class="section-card">
            <div class="section-title mb-4">
              <span
                class="section-badge bg-slate-100 text-slate-600 dark:bg-n-solid-3 dark:text-n-slate-11"
              >
                <span class="i-lucide-file-text size-4" />
              </span>
              <div>
                <h2 class="text-sm font-bold text-n-slate-12">
                  5. Additional Information
                </h2>
                <p class="text-xs text-n-slate-8">
                  Notes and follow-up details
                </p>
              </div>
            </div>
            <label class="label">
              Requirement Notes
              <span class="text-n-slate-6 font-normal">{{
                isEditMode ? '(internal notes)' : '(used in welcome message)'
              }}</span>
            </label>
            <textarea
              v-model="form.requirement_notes"
              rows="6"
              maxlength="500"
              placeholder="e.g. 3BHK in Patia, budget ₹80L"
              class="field !px-3 resize-none"
            />
            <p class="mt-1 text-xs text-n-slate-6 text-right">
              {{ form.requirement_notes.length }}/500
            </p>
            <p class="mt-1 text-xs text-n-slate-7">
              Preview: <em>{{ requirementSummary() }}</em>
            </p>
          </div>

          <!-- 6. Next Followup -->
          <div class="section-card">
            <div class="section-title mb-4">
              <span
                class="section-badge bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400"
              >
                <span class="i-lucide-calendar-clock size-4" />
              </span>
              <div>
                <h2 class="text-sm font-bold text-n-slate-12">
                  6. Next Followup
                </h2>
                <p class="text-xs text-n-slate-8">
                  Schedule your next follow-up with the lead
                </p>
              </div>
            </div>
            <label class="label">Next Follow-up Date &amp; Time</label>
            <div class="relative">
              <span class="i-lucide-calendar field-icon" />
              <input
                v-model="form.next_follow_up_at"
                type="datetime-local"
                class="field !pl-9 !pr-3"
              />
            </div>
          </div>

          <!-- Mark as verified (create mode) -->
          <div
            v-if="!isEditMode"
            class="rounded-xl border p-4 transition-colors"
            :class="
              markVerified
                ? 'border-green-300 bg-green-50 dark:bg-green-900/10'
                : 'border-n-weak bg-n-background'
            "
          >
            <div class="flex items-start gap-3">
              <input
                id="mark-verified"
                v-model="markVerified"
                type="checkbox"
                class="mt-0.5 size-4 rounded border-n-weak text-green-600 focus:ring-green-500 shrink-0"
              />
              <div>
                <label
                  for="mark-verified"
                  class="text-sm font-semibold text-n-slate-12 cursor-pointer select-none"
                >
                  Mark as verified lead
                </label>
                <p class="mt-0.5 text-xs text-n-slate-7">
                  Manual leads are verified by default because they are added
                  by a human agent. Verified leads appear in the
                  <strong>Verified Leads</strong> page immediately.
                </p>
                <p v-if="!markVerified" class="mt-1 text-xs text-yellow-700">
                  Unchecked: lead will be created as
                  <strong>unverified</strong> and won't appear in Verified
                  Leads until reviewed.
                </p>
              </div>
            </div>
          </div>

          <!-- WhatsApp Welcome (create mode only) -->
          <div
            v-if="!isEditMode"
            class="rounded-xl border p-4 transition-colors"
            :class="
              sendWelcome
                ? 'border-green-300 bg-green-50 dark:bg-green-900/10'
                : 'border-n-weak bg-n-background'
            "
          >
            <div class="flex items-start gap-3">
              <input
                id="send-welcome"
                v-model="sendWelcome"
                type="checkbox"
                class="mt-0.5 size-4 rounded border-n-weak text-green-600 focus:ring-green-500 shrink-0"
              />
              <div>
                <label
                  for="send-welcome"
                  class="text-sm font-semibold text-n-slate-12 cursor-pointer select-none"
                >
                  Send WhatsApp welcome message to customer
                </label>
                <p class="mt-0.5 text-xs text-n-slate-7">
                  Uses approved Meta template
                  <strong>nexceo_customer_welcome</strong>. Requires a phone
                  number and a configured WhatsApp inbox in NEXCEO Settings.
                </p>
                <div
                  v-if="sendWelcome"
                  class="mt-2 flex items-start gap-2 rounded-lg bg-n-background border border-n-weak px-3 py-2"
                >
                  <span
                    class="i-lucide-message-circle size-3.5 text-green-600 shrink-0 mt-0.5"
                  />
                  <p class="text-xs text-n-slate-8">
                    Preview: "Hello
                    <em>{{ form.name || 'Customer' }}</em
                    >, thank you for your interest in
                    <em>{{ selectedProject?.name || 'NEXCEO' }}</em
                    >. Your requirement: <em>{{ requirementSummary() }}</em
                    >."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sticky footer -->
    <div
      v-if="!isLoading"
      class="flex items-center justify-between gap-3 px-6 py-4 border-t border-n-weak bg-n-background shrink-0 flex-wrap"
    >
      <button
        type="button"
        class="px-5 py-2 text-sm text-n-slate-9 hover:text-n-slate-12 rounded-lg hover:bg-n-alpha-2 transition-colors"
        @click="goBack"
      >
        Cancel
      </button>
      <div class="flex items-center gap-3">
        <button
          type="button"
          :disabled="isSaving || isSavingDraft || (!isEditMode && !!saveResult)"
          class="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-woot-500 hover:bg-woot-600 rounded-lg disabled:opacity-50 transition-colors"
          @click="onSave"
        >
          <span v-if="isSaving" class="i-lucide-loader-2 size-4 animate-spin" />
          <span
            v-else
            :class="isEditMode ? 'i-lucide-save' : 'i-lucide-user-plus'"
            class="size-4"
          />
          {{
            isSaving ? (isEditMode ? 'Saving…' : 'Creating lead…') : pageTitle
          }}
        </button>
        <p class="text-xs text-n-slate-6 flex items-center gap-1 hidden md:flex">
          <span class="i-lucide-info size-3" />
          All fields marked <span class="text-red-500">*</span> are required
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.label {
  @apply block text-xs text-n-slate-9 mb-1 font-semibold;
}
.label-section {
  @apply text-sm font-semibold text-n-slate-10 uppercase tracking-wide mb-3;
}
/*
 * _base.scss has an unlayered, unguarded `select { border: none; ... }`
 * rule (and equivalents for button/textarea, plus a field-base mb-4 on
 * every input/select/textarea) that beats any @layer-scoped Tailwind
 * utility regardless of specificity — border, padding and margin are all
 * forced here with !important so every .field reliably renders its border
 * and respects the icon-gutter padding set per-field in the template, no
 * matter which element type it's applied to.
 */
.field {
  @apply w-full text-sm bg-white dark:bg-n-solid-3 text-n-slate-12 rounded-lg py-2 outline-none focus:ring-2 focus:ring-woot-500 transition-shadow;
  @apply border border-solid border-n-weak m-0 !important;
}
.field-icon {
  @apply absolute left-3 top-1/2 -translate-y-1/2 size-4 text-n-slate-7 pointer-events-none;
}
.section-card {
  @apply rounded-xl border border-n-weak bg-n-background p-4;
}
.section-title {
  @apply flex items-center gap-2.5;
}
.section-badge {
  @apply flex items-center justify-center size-8 rounded-lg shrink-0;
}
</style>
